import { Clock } from 'lucide-react';
import type { BatchDetail } from '../../api/types';
import { alertIcon, severityDot } from '../fournees/alerts';
import { cycleColor } from '../planning/colors';
import { ProgressBar, statusDot } from '../fournees/status';

const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const fmtDay = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' });

/** Carte de détail d'une fournée pour la TV : avancement global + tous les OF. */
export default function TvCard({ detail, badge }: { detail: BatchDetail; badge?: string }) {
  const c = detail.counters;
  const running = detail.status === 'RUNNING' || detail.status === 'LOADING' || detail.status === 'UNLOADING';
  const batchAlerts = detail.alerts.filter((a) => !a.codeOf);
  const color = cycleColor(detail.cycleCode, detail.cycleColor);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden h-full border-t-4" style={{ borderTopColor: color }}>
      {/* En-tête : cycle (chip couleur) + moyen bien lisibles */}
      <div className="px-3 py-2 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
        <div className="flex items-center gap-2 min-w-0">
          {badge && <span className="shrink-0 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">{badge}</span>}
          <span className="shrink-0 px-2 py-0.5 rounded text-white text-base font-bold" style={{ backgroundColor: color }}>{detail.cycleCode}</span>
          <span className="font-bold text-gray-900 dark:text-gray-100 text-xl truncate">{detail.resourceLabel}</span>
          <span className={`ml-auto shrink-0 w-3 h-3 rounded-full ${severityDot(detail.severity)}`} title="Alertes" />
        </div>
        {/* Date-heure clairement identifiable */}
        <div className="mt-1 flex items-center gap-2 text-base font-medium text-gray-700 dark:text-gray-300">
          <Clock size={16} className="text-gray-400 shrink-0" />
          <span>{fmtDay(detail.t0)} · {fmtTime(detail.t0)} ▸ {fmtTime(detail.t3)}</span>
          {running && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-500/30 dark:text-blue-200 font-semibold">EN COURS</span>}
          <span className="ml-auto text-sm text-gray-400 dark:text-gray-500">{detail.number}</span>
        </div>
      </div>

      {/* Avancement global de la fournée */}
      <div className="px-3 pt-2">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-0.5">
          <span>Avancement</span>
          <span className="font-semibold text-gray-700 dark:text-gray-200">{detail.progressPct}%</span>
        </div>
        <ProgressBar pct={detail.progressPct} height={7} />
      </div>

      <div className="px-3 py-2 grid grid-cols-2 gap-3 text-sm">
        <TvGauge label="Tc" used={c.tcUsed} max={c.tcMax} />
        <TvGauge label="Voies" used={c.vacuumUsed} max={c.vacuumMax} />
      </div>

      {batchAlerts.length > 0 && (
        <div className="px-3 pb-1 flex flex-wrap gap-1">
          {batchAlerts.map((a, i) => (
            <span key={i} className="text-base" title={a.message}>{alertIcon(a.code)}</span>
          ))}
        </div>
      )}

      {/* Tous les OF, déjà triés par statut (serveur) */}
      <div className="px-3 py-1.5 flex-grow overflow-y-auto">
        {detail.items.map((it) => {
          const worst = it.alerts[0];
          return (
            <div key={it.codeOf} className="flex items-center gap-1.5 text-base py-1 border-b border-gray-50 dark:border-gray-700/60 last:border-0">
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${statusDot(it.statusCategory)}`} title={it.statusCategory} />
              <span className="text-gray-800 dark:text-gray-100 shrink-0 font-medium">{it.codeOf}</span>
              <span className="text-gray-400 dark:text-gray-500 truncate text-sm">{it.label}</span>
              <span className="ml-auto text-xs text-gray-500 dark:text-gray-400 shrink-0 tabular-nums">{it.progressPct}%</span>
              {worst && <span className="text-sm shrink-0" title={it.alerts.map((a) => a.message).join(' · ')}>{alertIcon(worst.code)}</span>}
            </div>
          );
        })}
        {detail.items.length === 0 && <p className="text-sm text-gray-400 dark:text-gray-500 italic py-1">Aucun OF affecté.</p>}
      </div>
    </div>
  );
}

function TvGauge({ label, used, max }: { label: string; used: number; max: number }) {
  const ratio = max > 0 ? used / max : 0;
  const over = used > max;
  const color = over ? 'bg-red-500' : ratio > 0.9 ? 'bg-amber-500' : 'bg-green-500';
  return (
    <div>
      <div className="flex justify-between text-gray-500 dark:text-gray-400">
        <span>{label}</span>
        <span className={over ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-700 dark:text-gray-300'}>{max > 0 ? `${used}/${max}` : '—'}</span>
      </div>
      <div className="mt-0.5 h-2 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, ratio * 100)}%` }} />
      </div>
    </div>
  );
}
